import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { BallType, TrainingMode } from '../../enums/machine.enums';
import { IMachineState } from '../i-machine-state';
import { IMongoBase } from '../mongo/_base';
import { IBallState, IPlateLoc, ITrajectory } from '../pitches';
import { ISeamOrientation } from '../pitches/i-base';
import { IRapsodoBreak } from './i-rapsodo-shot';
import { IStereoVideo } from './i-stereo-video';

// originally based on IRapsodoConfidence but they renamed PITCH_SpeedConfidence to PITCH_ReleaseSpeedConfidence
// we keep this shape to ensure our shots don't look different after the change
export interface IShotConfidence {
  /** between 0.0-1.0 where 1.0 === 100% */
  PITCH_SpeedConfidence: number;
  /** between 0.0-1.0 where 1.0 === 100% */
  PITCH_SpinConfidence: number;
  /** between 0.0-1.0 where 1.0 === 100% */
  PITCH_StrikePositionHeightConfidence: number;
  /** between 0.0-1.0 where 1.0 === 100% */
  PITCH_StrikePositionSideConfidence: number;
}

export const DEFAULT_FULL_CONFIDENCE: IShotConfidence = {
  PITCH_SpeedConfidence: 1,
  PITCH_SpinConfidence: 1,
  PITCH_StrikePositionHeightConfidence: 1,
  PITCH_StrikePositionSideConfidence: 1,
};

export interface IMachineShot extends IMongoBase {
  /** _id of pitch that resulted in this shot */
  pitch_id?: string;

  trackman_pitch_id?: string;
  trackman_hit_id?: string;

  rapsodo_shot_id?: string;
  msbs_shot_id?: string;

  session: string;
  machineID: string;

  // the targeted plate location from the mstarget (might not exist for old shots)
  plate?: IPlateLoc;

  // e.g. when a shot is created from ball type calibration or repeatability, for easier recall
  collectionID?: string;

  // for data collection, the first few shots are for aiming and should be excluded from analysis
  aiming?: boolean;

  target_ms: IMachineState;
  actual_ms: IMachineState;

  // target from the mstarget, for evaluation purposes
  target_bs?: IBallState;
  // target from the mstarget, for evaluation purposes
  target_traj?: ITrajectory;

  // actual from the detection
  bs?: IBallState;

  /** describes where the actual bs came from */
  bs_origin: string;
  // actual from the detection
  traj: ITrajectory;

  /** when using simple training, if user input is required */
  user_traj?: ITrajectory;

  ball_type?: BallType;

  timestamp: number;

  confidence?: IShotConfidence;
  break?: IRapsodoBreak;

  seams?: ISeamOrientation;

  tracking_device?: TrackingDevice;

  training_mode?: TrainingMode;
  training: boolean;

  /** e.g. for quick train, if this shot completes training for a pitch, mark it as true to ignore min training threshold for the machine */
  training_complete?: boolean;

  /** based on the active preset at the time when training_complete was set to true */
  precision_trained?: boolean;

  /** do not use shots where a hitter was (likely) present for aiming purposes */
  hitter_present?: boolean;

  /** reasons for low confidence */
  warnings?: string[];

  archived?: boolean;

  // from MSBS payload
  bi_timeout?: boolean;

  // should match the fire event's video
  video?: IStereoVideo;
}

export interface IMatchingShotsRequest {
  matching_hashes: string[];
  machineID: string;
  ball_type: BallType;

  /** ISO string of a date */
  newerThan?: string;

  includeLowConfidence: boolean;
  includeHitterPresent: boolean;
}

export interface IArchiveMachineShotsByHashRequest {
  machineID: string;
  ball_type: BallType;
  matching_hashes: string[];
}

export interface IArchiveMachineShotsByDateRequest {
  machineID: string;
  ball_type: BallType;
  start_date?: string;
  end_date?: string;
}

export interface IAggregateMachineShotsRequest {
  machineID: string;

  // if not provided, the active ball type for the machine will be used
  ball_type?: BallType;

  matching_hash?: string;

  start_date?: string;
  end_date?: string;

  includeLowConfidence: boolean;
  includeHitterPresent: boolean;
}
export interface IAggregateMachineShotsEntry {
  // how many shots in total have this hash
  total: number;
  // quick train
  qt: number;
  // quick train with at least one training_complete === true
  qt_complete: boolean;
  // whether by quick train or if shots are at least the threshold amount
  trained?: boolean;
}

export const DEFAULT_AGG_SHOTS_ENTRY: IAggregateMachineShotsEntry = {
  total: 0,
  qt: 0,
  qt_complete: false,
  trained: false,
};

export interface IAggregateMachineShotsDict {
  [matching_hash: string]: IAggregateMachineShotsEntry;
}

export interface IMatchingShotsDict {
  /** would be undefined if not set yet */
  [matching_hash: string]: IMachineShot[];
}

export interface IAggregateCollectionRequest {
  machineID: string;
  collectionID: string;

  matching_hash?: string;

  includeHitterPresent: boolean;
  includeLowConfidence: boolean;
}

export interface IAggregateCollectionResult {
  pitch_id: string;
  matching_hash: string;
  total: number;
  aiming: number;
  analysis: number;
}
